import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Button,
  Chip,
  TextField,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  NavigateNext as NavigateNextIcon,
  Verified as VerifiedIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon,
  Summarize as SummarizeIcon,
} from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import Select from '../../../components/Select';
import DateRangePicker from '../../../components/DateRangePicker';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import VerifyCitizenModal from './VerifyCitizenModal';
import {
  fetchLogs,
  fetchInit,
  selectLogs,
  selectLoading,
  selectError,
  selectSuccessMessage,
  selectTotal,
  selectPage,
  selectPageSize,
  selectSortColumn,
  selectSortDirection,
  selectFilterStartDate,
  selectFilterEndDate,
  selectFilterStatus,
  selectFilterIdentityNumber,
  selectFilterUnitId,
  selectFilterDeviceId,
  selectInitData,
  selectExportLoading,
  selectReportLoading,
  selectReportData,
  selectReportModalOpen,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterStartDate,
  setFilterEndDate,
  setFilterStatus,
  setFilterIdentityNumber,
  setFilterUnitId,
  setFilterDeviceId,
  resetFilters,
  clearError,
  clearSuccessMessage,
  exportToExcel,
  fetchReport,
  setReportModalOpen,
  clearReportData,
} from './slice';

function LGSPIntegration() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const logs = useSelector(selectLogs);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const successMessage = useSelector(selectSuccessMessage);
  const total = useSelector(selectTotal);
  const page = useSelector(selectPage);
  const pageSize = useSelector(selectPageSize);
  const sortColumn = useSelector(selectSortColumn);
  const sortDirection = useSelector(selectSortDirection);
  const filterStartDate = useSelector(selectFilterStartDate);
  const filterEndDate = useSelector(selectFilterEndDate);
  const filterStatus = useSelector(selectFilterStatus);
  const filterIdentityNumber = useSelector(selectFilterIdentityNumber);
  const filterUnitId = useSelector(selectFilterUnitId);
  const filterDeviceId = useSelector(selectFilterDeviceId);
  const initData = useSelector(selectInitData);
  const exportLoading = useSelector(selectExportLoading);
  const reportLoading = useSelector(selectReportLoading);
  const reportData = useSelector(selectReportData);
  const reportModalOpen = useSelector(selectReportModalOpen);

  const [verifyModalOpen, setVerifyModalOpen] = useState(false);

  // Get statistics from initData API
  const statistics = useMemo(() => {
    const successCount = initData?.countQuerySuccess || 0;
    const failureCount = initData?.countQueryFailed || 0;
    const totalQuery = initData?.totalQuery || 0;
    const totalTimeMs = initData?.totalTimeResponse || 0;

    const totalTimeSec = (totalTimeMs / 1000).toFixed(2);
    const avgTimeSec = totalQuery > 0 ? (totalTimeMs / totalQuery / 1000).toFixed(2) : 0;

    return {
      totalTimeSec,
      avgTimeSec,
      queryCount: totalQuery,
      successCount,
      failureCount,
      totalRequests: successCount + failureCount,
    };
  }, [initData]);

  // Status options from init data
  const statusOptions = [
    { value: '', label: t('lgspIntegration.filters.all') },
    ...(initData?.statuses || []).map((s) => ({ value: s.id, label: s.name })),
  ];

  // Request type options from init data
  const requestTypeOptions = [
    { value: '', label: t('lgspIntegration.filters.all') },
    ...(initData?.requestTypes || []).map((r) => ({ value: r.id, label: r.name })),
  ];

  // Unit options from init data
  const unitOptions = useMemo(() => [
    { id: '', name: t('lgspIntegration.filters.all') },
    ...(initData?.units || []),
  ], [initData?.units, t]);

  // Device options from init data (filtered by selected unit)
  const deviceOptions = useMemo(() => {
    const allDevices = initData?.devices || [];
    // Filter devices by selected unit
    const filteredDevices = filterUnitId
      ? allDevices.filter((d) => d.unitId === filterUnitId)
      : allDevices;
    return [
      { id: '', name: t('lgspIntegration.filters.all') },
      ...filteredDevices,
    ];
  }, [initData?.devices, filterUnitId, t]);

  // Build filter params for API call
  const buildFilterParams = () => ({
    page,
    pageSize,
    sortColumn,
    sortDirection,
    ...(filterStartDate && { startDate: filterStartDate }),
    ...(filterEndDate && { endDate: filterEndDate }),
    ...(filterStatus !== null && filterStatus !== '' && { status: filterStatus }),
    ...(filterIdentityNumber && { identityNumber: filterIdentityNumber }),
    ...(filterUnitId && { unitId: filterUnitId }),
    ...(filterDeviceId && { deviceId: filterDeviceId }),
  });

  // Fetch init data and logs on mount
  useEffect(() => {
    dispatch(fetchInit());
    dispatch(fetchLogs(buildFilterParams()));
  }, [dispatch]);

  // Refetch logs when pagination/sorting changes
  useEffect(() => {
    dispatch(fetchLogs(buildFilterParams()));
  }, [dispatch, page, pageSize, sortColumn, sortDirection]);

  // Handle search button click
  const handleSearch = () => {
    dispatch(setPage(1)); // Reset to first page when searching
    dispatch(fetchLogs(buildFilterParams()));
  };

  // Handle clear filters
  const handleClearFilters = () => {
    dispatch(resetFilters());
    dispatch(fetchLogs({ page: 1, pageSize, sortColumn, sortDirection }));
  };

  // Handle export to Excel
  const handleExportToExcel = () => {
    dispatch(exportToExcel(buildFilterParams()));
  };

  // Handle open report modal
  const handleOpenReportModal = () => {
    if (!filterStartDate || !filterEndDate) {
      showNotification(t('lgspIntegration.report.selectDateRange'), 'warning');
      return;
    }
    dispatch(setReportModalOpen(true));
    dispatch(fetchReport({
      startDate: filterStartDate,
      endDate: filterEndDate,
      periodType: 'Custom',
      ...(filterUnitId && { unitId: filterUnitId }),
      ...(filterDeviceId && { deviceId: filterDeviceId }),
    }));
  };

  // Handle close report modal
  const handleCloseReportModal = () => {
    dispatch(setReportModalOpen(false));
    dispatch(clearReportData());
  };

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(t(successMessage), 'success');
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch, showNotification, t]);

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 0: // Success
        return 'success';
      case 1: // Failed
        return 'error';
      case 2: // Pending
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      key: 'requestTime',
      label: t('lgspIntegration.table.requestTime'),
      sortable: true,
      render: (value) => formatDateTimeWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'licenseCode',
      label: t('lgspIntegration.table.licenseCode'),
      sortable: true,
    },
    {
      key: 'deviceCode',
      label: t('lgspIntegration.table.deviceCode'),
      sortable: true,
    },
    {
      key: 'unitName',
      label: t('lgspIntegration.table.unitName'),
      sortable: true,
    },
    {
      key: 'requestTypeDisplay',
      label: t('lgspIntegration.table.requestType'),
      sortable: false,
    },
    {
      key: 'identityNumber',
      label: t('lgspIntegration.table.identityNumber'),
      sortable: true,
    },
    {
      key: 'statusDisplay',
      label: t('lgspIntegration.table.status'),
      sortable: false,
      render: (value, row) => (
        <Tooltip title={value} arrow placement="top">
          <Chip
            label={value}
            color={getStatusColor(row.status)}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      key: 'responseTimeMs',
      label: t('lgspIntegration.table.responseTime'),
      sortable: true,
      render: (value) => `${value} ms`,
    },
    {
      key: 'dailyCallCount',
      label: t('lgspIntegration.table.dailyCallCount'),
      sortable: true,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon fontSize="small" />
            <Typography variant="body2">{t('lgspIntegration.breadcrumb.reports')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('lgspIntegration.breadcrumb.lgspIntegration')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('lgspIntegration.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<VerifiedIcon />}
            onClick={() => setVerifyModalOpen(true)}
          >
            {t('lgspIntegration.verifyButton')}
          </Button>
        </Box>

        {/* Statistics Boxes */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Box 1: Response Time */}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              minWidth: 280,
              flex: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light',
              backgroundColor: 'primary.50',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AccessTimeIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                {t('lgspIntegration.stats.responseTime')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                ({statistics.avgTimeSec} s/{t('lgspIntegration.stats.perQuery')})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('lgspIntegration.stats.totalTime')}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {statistics.totalTimeSec} s
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('lgspIntegration.stats.queryCount')}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {statistics.queryCount} {t('lgspIntegration.stats.times')}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Box 2: Success Rate */}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              minWidth: 280,
              flex: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'success.light',
              backgroundColor: 'success.50',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                {t('lgspIntegration.stats.successRate')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                ({statistics.successCount}/{statistics.totalRequests})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('lgspIntegration.stats.successCount')}:
                </Typography>
                <Typography variant="body2" fontWeight="medium" color="success.main">
                  {statistics.successCount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('lgspIntegration.stats.failureCount')}:
                </Typography>
                <Typography variant="body2" fontWeight="medium" color="error.main">
                  {statistics.failureCount}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Filter Section */}
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DateRangePicker
                label={t('lgspIntegration.filters.dateRange')}
                fromValue={filterStartDate || ''}
                toValue={filterEndDate || ''}
                onFromChange={(e) => dispatch(setFilterStartDate(e.target.value))}
                onToChange={(e) => dispatch(setFilterEndDate(e.target.value))}
                id="filterDateRange"
                name="filterDateRange"
                data-testid="lgsp-daterangepicker"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Select
                label={t('lgspIntegration.filters.unit')}
                value={filterUnitId ?? ''}
                onChange={(e) => {
                  const newValue = e?.target?.value;
                  // Convert to number if it's a numeric string, or null if empty
                  const unitId = newValue === '' || newValue === null || newValue === undefined
                    ? null
                    : typeof newValue === 'number' ? newValue : parseInt(newValue, 10) || null;
                  dispatch(setFilterUnitId(unitId));
                }}
                options={unitOptions}
                id="filterUnitId"
                name="filterUnitId"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Select
                label={t('lgspIntegration.filters.device')}
                value={filterDeviceId ?? ''}
                onChange={(e) => {
                  const newValue = e?.target?.value;
                  // Convert to number if it's a numeric string, or null if empty
                  const deviceId = newValue === '' || newValue === null || newValue === undefined
                    ? null
                    : typeof newValue === 'number' ? newValue : parseInt(newValue, 10) || null;
                  dispatch(setFilterDeviceId(deviceId));
                }}
                options={deviceOptions}
                id="filterDeviceId"
                name="filterDeviceId"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('lgspIntegration.filters.status')}
                value={filterStatus ?? ''}
                onChange={(e) => dispatch(setFilterStatus(e.target.value === '' ? null : Number(e.target.value)))}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={t('lgspIntegration.filters.identityNumber')}
                value={filterIdentityNumber || ''}
                onChange={(e) => dispatch(setFilterIdentityNumber(e.target.value))}
                placeholder={t('lgspIntegration.filters.identityNumber')}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ minWidth: 120 }}
            >
              {t('lgspIntegration.filters.search')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ minWidth: 120 }}
            >
              {t('lgspIntegration.filters.clear')}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={exportLoading}
              sx={{ minWidth: 150 }}
            >
              {t('lgspIntegration.exportExcel')}
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<SummarizeIcon />}
              onClick={handleOpenReportModal}
              sx={{ minWidth: 150 }}
            >
              {t('lgspIntegration.report.createReport')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="lgsp-logs-table"
            columns={columns}
            data={logs}
            selectable={false}
            loading={loading}
            emptyMessage={t('lgspIntegration.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
            onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={(col, dir) => {
              dispatch(setSortColumn(col));
              dispatch(setSortDirection(dir));
            }}
          />
        </Box>
      </Paper>

      {/* Verify Citizen Modal */}
      <VerifyCitizenModal
        open={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
      />

      {/* Report Modal */}
      <Dialog
        open={reportModalOpen}
        onClose={handleCloseReportModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SummarizeIcon color="primary" />
            <Typography variant="h6">{t('lgspIntegration.report.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {reportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reportData ? (
            <Box>
              {/* Report Period */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('lgspIntegration.report.period')}: {filterStartDate} - {filterEndDate}
              </Typography>

              {/* Summary Statistics */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('lgspIntegration.report.summary')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                      <Typography variant="h4" color="primary.main">{reportData.totalRequests}</Typography>
                      <Typography variant="body2">{t('lgspIntegration.report.totalRequests')}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                      <Typography variant="h4" color="success.main">{reportData.successRequests}</Typography>
                      <Typography variant="body2">{t('lgspIntegration.report.successRequests')}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                      <Typography variant="h4" color="error.main">{reportData.failedRequests}</Typography>
                      <Typography variant="body2">{t('lgspIntegration.report.failedRequests')}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                      <Typography variant="h4" color="info.main">{reportData.successRate}%</Typography>
                      <Typography variant="body2">{t('lgspIntegration.report.successRate')}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Additional Stats */}
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('lgspIntegration.report.avgResponseTime')}
                    </Typography>
                    <Typography variant="h6">{reportData.averageResponseTime} ms</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('lgspIntegration.report.uniqueUnits')}
                    </Typography>
                    <Typography variant="h6">{reportData.uniqueUnits}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('lgspIntegration.report.uniqueDevices')}
                    </Typography>
                    <Typography variant="h6">{reportData.uniqueDevices}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* By Unit Statistics */}
              {reportData.byUnit && reportData.byUnit.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('lgspIntegration.report.byUnit')}
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {reportData.byUnit.map((unit, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                        <Typography variant="body2">{unit.unitName || t('lgspIntegration.report.unknown')}</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Chip size="small" label={`${unit.totalRequests} ${t('lgspIntegration.report.requests')}`} />
                          <Chip size="small" color="success" label={`${unit.successRate}%`} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* By Device Statistics */}
              {reportData.byDevice && reportData.byDevice.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('lgspIntegration.report.byDevice')}
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {reportData.byDevice.map((device, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                        <Typography variant="body2">{device.deviceCode || t('lgspIntegration.report.unknown')}</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Chip size="small" label={`${device.totalRequests} ${t('lgspIntegration.report.requests')}`} />
                          <Chip size="small" color="success" label={`${device.successRate}%`} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">{t('lgspIntegration.noData')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportModal}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LGSPIntegration;
